// AddSignatureModal.tsx
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCreateSignMutation } from "../../../../Services/settingsAPIs/SignatureApi";
import { toast } from "react-toastify";
import { useUsersByTeamIdQuery } from "../../../../Services/ticket/users/UsersByTeamId";

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  onSaveSuccess: () => void;
  selectedTeam: number;
  emailIMAPmailConfigID: any;
  existingSignatures?: any[];
  activeTab: "default" | "users" | "live"; // Add this prop
}

const AddSignatureModal: React.FC<Props> = ({
  isOpen,
  closeModal,
  onSaveSuccess,
  selectedTeam,
  emailIMAPmailConfigID,
  existingSignatures = [],
  activeTab, // Receive activeTab
}) => {
  const [editorContent, setEditorContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const [createSignature] = useCreateSignMutation();

  const { data: usersData, isLoading: usersLoading } = useUsersByTeamIdQuery(
    selectedTeam,
    {
      skip: !selectedTeam,
    }
  );

  // Determine if this should be a default signature based on active tab
  const isDefault = activeTab === "default";

  // Check if a default signature already exists
  const hasDefaultSignature = existingSignatures.some(
    (sig) => sig.is_default && sig.smtp_configuration === emailIMAPmailConfigID
  );

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setEditorContent("");
    setSelectedUserId(null);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Signature name is required"),
    }),
    onSubmit: async (values) => {
      if (!editorContent || editorContent.trim() === "<p><br></p>") {
        toast.error("Signature content is required");
        return;
      }

      // Only require user selection when on users tab
      if (!isDefault && !selectedUserId) {
        toast.error("Please select a user for individual signature");
        return;
      }

      // Prevent creating default if one already exists and we're on default tab
      if (isDefault && hasDefaultSignature) {
        toast.error(
          "A default signature already exists. Please delete the existing default signature first."
        );
        return;
      }

      setIsSaving(true);

      try {
        const payload = {
          name: values.name.trim(),
          signature_html: editorContent,
          smtp_configuration: emailIMAPmailConfigID,
          is_default: isDefault,
          user: isDefault ? null : selectedUserId,
          team_id: selectedTeam,
        };

        console.log("Create Signature Payload:", payload);
        await createSignature(payload).unwrap();
        toast.success(
          `${isDefault ? "Default" : "User"} signature created successfully`
        );

        resetForm();
        onSaveSuccess();
        closeModal();
      } catch (error: any) {
        const errData = error?.data;

        // Handle "Only admins/managers can manage email signatures."
        if (errData?.detail) {
          toast.error(errData.detail);
        } else if (errData?.is_default) {
          toast.error(errData.is_default[0]);
        } else if (errData?.name) {
          toast.error(errData.name[0]);
        } else if (errData?.signature_html) {
          toast.error(errData.signature_html[0]);
        } else if (errData?.smtp_configuration) {
          toast.error(errData.smtp_configuration[0]);
        } else if (errData?.user) {
          toast.error(errData.user[0]);
        } else if (errData?.message) {
          toast.error(errData.message);
        } else {
          toast.error("Failed to create signature, please try again.");
        }
      } finally {
        setIsSaving(false);
      }
    },
    enableReinitialize: false,
  });

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedUserId(value ? Number(value) : null);
  };

  const handleClose = () => {
    if (!isSaving) {
      resetForm();
      closeModal();
    }
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "align",
    "script",
    "background",
    "code-block",
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }, { font: [] }],
      ["bold", "italic", "underline", "strike"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "blockquote", "code-block"],
      [{ color: [] }, { background: [] }, { align: [] }],
      ["clean"],
    ],
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Add signature"
      ariaHideApp={false}
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "20px",
          borderRadius: "8px",
          border: "none",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1050,
        },
      }}
      shouldCloseOnOverlayClick={!isSaving}
    >
      <button
        onClick={handleClose}
        disabled={isSaving}
        style={{
          float: "right",
          fontSize: "18px",
          border: "none",
          background: "none",
          cursor: "pointer",
        }}
        aria-label="Close modal"
      >
        &times;
      </button>
      <h2>{isDefault ? "Add default signature" : "Add user signature"}</h2>

      <form onSubmit={formik.handleSubmit}>
        {/* Info Alert */}
        <div
          className={`alert ${isDefault ? "alert-info" : "alert-success"} mb-3`}
        >
          <i
            className={`bi ${
              isDefault ? "bi-star-fill" : "bi-person-check-fill"
            } me-2`}
          ></i>
          {isDefault
            ? "This signature will be applied to all team members by default"
            : "This signature will be assigned to a specific user"}
        </div>

        {/* Show warning if default already exists */}
        {isDefault && hasDefaultSignature && (
          <div className="alert alert-warning mb-3">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>A default
            signature already exists. Please delete it first before creating a
            new one.
          </div>
        )}

        {/* User Selection Dropdown - Only show on users tab */}
        {!isDefault && (
          <div className="mb-3">
            <label htmlFor="userSelect" className="form-label fw-semibold">
              <i className="bi bi-person me-2"></i>
              Select user <span className="text-danger">*</span>
            </label>
            {usersLoading ? (
              <div className="text-muted small">Loading users...</div>
            ) : (
              <select
                className="form-select"
                id="userSelect"
                value={selectedUserId || ""}
                onChange={handleUserChange}
                disabled={isSaving}
              >
                <option value="">-- Select a user --</option>
                {usersData?.map((user: any) => (
                  <option value={user.id} key={user.id}>
                    {user.name || user.username}
                  </option>
                ))}
              </select>
            )}
            <small className="text-muted d-block mt-1">
              <i className="bi bi-info-circle me-1"></i>
              This signature will only be used by the selected user
            </small>
          </div>
        )}

        <label htmlFor="name" className="form-label mt-3">
          Signature name <span className="text-danger">*</span>
        </label>
        <input
          id="name"
          name="name"
          className="form-control"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isSaving}
          placeholder={
            isDefault
              ? "e.g., Default Team Signature"
              : "e.g., John's Signature"
          }
        />
        {formik.touched.name && formik.errors.name && (
          <div className="text-danger">{formik.errors.name}</div>
        )}

        <label className="form-label mt-3">
          Signature content <span className="text-danger">*</span>
        </label>
        <ReactQuill
          value={editorContent}
          onChange={setEditorContent}
          modules={modules}
          formats={formats}
          readOnly={isSaving}
          placeholder="Type your signature here..."
        />

        <div className="mt-4 text-end">
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving || (isDefault && hasDefaultSignature)}
          >
            {isSaving ? "Creating..." : "Create signature"}
          </button>
        </div>
      </form>
    </ReactModal>
  );
};

export default AddSignatureModal;
